import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { matchRoutes, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { qrcodeAPIService } from '~/api/qrcode';
import { BackdropFullScreen } from '~/components/atoms/loader/BackdropFullScreen';
import { WhatsappMockup } from '~/components/atoms/mockup-previews/whatsapp-mockup/WhatsappMockup';
import { ConfirmNavigateModal } from '~/components/organisms/ConfirmNavigateModal';
import { QR_CODE_FOLDER_FORM_NAME, QRCODE_STATIC_STEP, QRCODE_STATIC_TYPE } from '~/constants/qr-code';
import { PATH } from '~/constants/route-path';
import useQrCodeData from '~/hooks/useQrCodeData';
import { useZodForm } from '~/hooks/useZodForm';
import { StaticQrContentLayout } from '~/pages/qrBuilder/p-static/components/static-qr-content-layout/StaticQrContentLayout';
import { StaticQrCodeData } from '~/types/qrCode';
import { mapQrTypeForApi } from '~/utils/qr-code';

import { StaticWhatsappForm } from './components/Form';
import { getSchema } from './utils/form-schema';

export const EditStaticWhatsappContent = () => {
  const navigate = useNavigate();
  const form = useZodForm({ getSchema });
  const [{ params }] = matchRoutes([{ path: PATH.QR.EDIT_QR_STATIC(QRCODE_STATIC_STEP.CONTENT) }], location);

  const values = form.watch();

  const { data: qrCode, isLoading: qrCodeIsLoading } = useQrCodeData<StaticQrCodeData>({ qrCodeId: params.id });

  const isChangeQrType = params.type !== qrCode?.content.type;

  const changeStaticQrTypeMutation = useMutation({ mutationFn: qrcodeAPIService.changeStaticQrType });
  const updateStaticQrWhatsappMutation = useMutation({ mutationFn: qrcodeAPIService.updateStaticQrWhatsapp });

  const isNextProcessing = changeStaticQrTypeMutation.isPending || updateStaticQrWhatsappMutation.isPending;

  useEffect(() => {
    if (qrCode) {
      const { number, message } = qrCode.content;

      if (!isChangeQrType) {
        form.setValue('number', number);
        form.setValue('message', message);
      }

      form.setValue('name', qrCode.name);
      // form.setValue('passwordProtection.isPasswordProtected', qrCode.isPasswordProtected ?? false);

      if (qrCode.folder) {
        form.setValue(QR_CODE_FOLDER_FORM_NAME, qrCode.folder.id);
      }
    }
  }, [qrCode?.id]);

  const onSubmit = (values) => {
    if (isNextProcessing) return;

    const { name, message, number } = values;

    const payload = {
      ...(isChangeQrType ? { type: mapQrTypeForApi(params.type) } : {}),
      id: params.id,
      body: {
        name,
        content: { number, message },
        ...(values.qrCodeFolder ? { folder_id: values.qrCodeFolder } : {}),
        // isPasswordProtected: values.passwordProtection.isPasswordProtected,
        // password: values.passwordProtection.password,
      },
    };

    const mutation = isChangeQrType ? changeStaticQrTypeMutation : updateStaticQrWhatsappMutation;

    mutation.mutate(payload, {
      onSuccess: () => {
        navigate(PATH.QR.EDIT_QR_STATIC(QRCODE_STATIC_STEP.STYLES, QRCODE_STATIC_TYPE.WHATS_APP, params.id));
      },
    });
  };

  const handleClickStep = async (step) => {
    if (step === 0) navigate(PATH.QR.EDIT_QR_STATIC(QRCODE_STATIC_STEP.TYPE, QRCODE_STATIC_TYPE.WHATS_APP, params.id));
    if (step === 2) await form.handleSubmit(onSubmit)();
  };

  return (
    <FormProvider {...form}>
      <StaticQrContentLayout
        handleStep={handleClickStep}
        isNextProcessing={isNextProcessing}
        activeStep={1}
        qrCode={qrCode}
        mockup={<WhatsappMockup number={values.number} message={values.message} />}
        form={
          <BackdropFullScreen isLoading={qrCodeIsLoading}>
            <StaticWhatsappForm />
          </BackdropFullScreen>
        }
      />

      <ConfirmNavigateModal activeStep={1} />
    </FormProvider>
  );
};
